import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  CreditCard, Plus, Search, User, DollarSign, Filter, FileText,
  CheckCircle, Clock, XCircle, AlertCircle, Package, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  draft: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText },
  finalized: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  paid: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  void: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  // Legacy status mapping
  Unpaid: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertCircle },
  Paid: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle }
};

const STATUS_LABELS = {
  draft: 'Draft',
  finalized: 'Finalized',
  paid: 'Paid',
  void: 'Void',
  Unpaid: 'Unpaid',
  Paid: 'Paid'
};

const Billing = () => {
  const { isOwner, isDoctor } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const fetchData = useCallback(async () => {
    try {
      const [invoicesRes, patientsRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/patients`)
      ]);
      setInvoices(invoicesRes.data);
      setPatients(patientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const patientId = searchParams.get('patient_id');
    if (searchParams.get('action') === 'new') {
      setShowModal(true);
      if (patientId) {
        setFormData(prev => ({ ...prev, patient_id: patientId }));
      }
    }
  }, [fetchData, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patient_id) {
      toast.error('Please select a patient');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/invoices`, formData);
      toast.success('Invoice created successfully');
      setShowModal(false);
      setFormData({ patient_id: '', notes: '' });
      // Navigate to the new invoice
      navigate(`/billing/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const getStatusStyle = (status) => {
    return STATUS_STYLES[status] || STATUS_STYLES.draft;
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = 
      inv.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate totals
  const totalDraft = invoices.filter(i => i.status === 'draft').reduce((sum, i) => sum + (i.grand_total || i.amount || 0), 0);
  const totalFinalized = invoices.filter(i => i.status === 'finalized').reduce((sum, i) => sum + (i.grand_total || i.amount || 0), 0);
  const totalPaid = invoices.filter(i => i.status === 'paid' || i.status === 'Paid').reduce((sum, i) => sum + (i.grand_total || i.amount || 0), 0);
  const totalUnpaid = invoices.filter(i => i.status === 'Unpaid').reduce((sum, i) => sum + (i.grand_total || i.amount || 0), 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD' }).format(amount || 0);
  };

  // Doctors can only view
  const canCreateInvoice = isOwner || !isDoctor;

  return (
    <Layout>
      <div className="page-container" data-testid="billing-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Billing</h1>
            <p className="text-slate-600 mt-1">{invoices.length} total invoices</p>
          </div>
          <div className="flex items-center gap-3">
            {isOwner && (
              <Button
                variant="outline"
                onClick={() => navigate('/services')}
                className="border-2 flex items-center gap-2"
              >
                <Package className="w-4 h-4" />
                Service Catalogue
              </Button>
            )}
            {canCreateInvoice && (
              <Button
                onClick={() => setShowModal(true)}
                className="btn-primary flex items-center gap-2"
                data-testid="add-invoice-btn"
              >
                <Plus className="w-4 h-4" />
                New Invoice
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card p-4" data-testid="draft-total">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Draft</p>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(totalDraft)}</p>
              </div>
            </div>
          </div>
          <div className="card p-4" data-testid="finalized-total">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Finalized</p>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(totalFinalized + totalUnpaid)}</p>
              </div>
            </div>
          </div>
          <div className="card p-4" data-testid="paid-total">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Paid</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(totalPaid)}</p>
              </div>
            </div>
          </div>
          <div className="card p-4" data-testid="total-revenue">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Revenue</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(totalPaid)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="finalized">Finalized</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Invoice List */}
        <div className="card" data-testid="invoices-list">
          <div className="p-4 border-b-2 border-slate-200">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Invoices ({filteredInvoices.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : filteredInvoices.length > 0 ? (
            <div className="divide-y-2 divide-slate-100">
              {filteredInvoices.map((invoice) => {
                const statusStyle = getStatusStyle(invoice.status);
                const StatusIcon = statusStyle.icon;
                const amount = invoice.grand_total || invoice.amount || 0;
                const lineItemCount = invoice.line_items?.length || 0;
                
                return (
                  <div
                    key={invoice.id}
                    className="p-4 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/billing/${invoice.id}`)}
                    data-testid={`invoice-item-${invoice.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusStyle.color.split(' ')[0]}`}>
                          <StatusIcon className={`w-5 h-5 ${statusStyle.color.split(' ')[1]}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-slate-600">{invoice.invoice_number}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusStyle.color}`}>
                              {STATUS_LABELS[invoice.status] || invoice.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <User className="w-3 h-3 text-slate-400" />
                            <span className="font-medium text-slate-900">{invoice.patient_name}</span>
                          </div>
                          {lineItemCount > 0 && (
                            <p className="text-xs text-slate-500 mt-1">
                              {lineItemCount} item{lineItemCount !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-slate-900">{formatCurrency(amount)}</p>
                        <p className="text-sm text-slate-400">{invoice.created_at?.split('T')[0]}</p>
                        {invoice.encounter_id && (
                          <p className="text-xs text-blue-600 mt-1 flex items-center justify-end gap-1">
                            <Eye className="w-3 h-3" /> Linked to encounter
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No invoices found</p>
              <p className="text-sm text-slate-500">
                {searchTerm || statusFilter !== 'all' ? 'Try different filters' : 'Create your first invoice'}
              </p>
            </div>
          )}
        </div>

        {/* Create Invoice Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md border-2 border-slate-200" data-testid="add-invoice-modal">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Create Invoice
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div>
                <Label htmlFor="patient_id">Patient *</Label>
                <Select
                  value={formData.patient_id}
                  onValueChange={(value) => setFormData({ ...formData, patient_id: value })}
                >
                  <SelectTrigger data-testid="invoice-patient-select">
                    <SelectValue placeholder="Select a patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((patient) => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any notes for this invoice..."
                  data-testid="invoice-notes-input"
                />
              </div>
              <p className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                After creating the invoice, you can add line items from the Service Catalogue.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-2">
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving} data-testid="save-invoice-btn">
                  {saving ? 'Creating...' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Billing;
